<template>
  <div
    :class="[
      'single-testimonial mx-auto max-w-screen-xl',
      'slug-' + singleTestimonial?.testimonial?.slug,
      loading ? 'cursor-wait' : '',
    ]"
  >
    <OrganismsBWTextOverlay
      v-if="brandWindow"
      class="h-[500px]"
      :organism="brandWindow"
    />
    <div
      class="flex flex-col md:grid md:grid-cols-[242px_1fr] max-w-5xl mx-auto md:gap-16 pt-14 px-8"
    >
      <aside class="order-last sidebar md:order-none">
        <OrganismsSidebarTestimonials />
      </aside>
      <main class="max-w-full pb-20 overflow-hidden">
        <h2
          v-if="singleTestimonial?.testimonial?.customPostDetails.subtitle"
          class="mb-6 text-2xl font-semibold text-gray-900 subtitle font-archer md:text-5xl"
        >
          {{ singleTestimonial?.testimonial?.customPostDetails.subtitle }}
        </h2>
        <span class="mb-8 text-base font-bold date font-arial line-clamp-1">{{
          convertDateFormat(singleTestimonial?.testimonial?.date) ?? ""
        }}</span>
        <div
          v-if="loading"
          class="p-8 font-medium text-center text-gray-500 font-ubuntu"
        >
          Loading...
        </div>
        <div
          v-if="
            !loading && singleTestimonial?.testimonial?.testimonials?.videoUrl
          "
          class="w-full mb-8 video-wrapper"
          v-html="
            getEmbedCodeByUrl(
              singleTestimonial?.testimonial?.testimonials?.videoUrl,
            )
          "
        ></div>
        <div
          v-if="!loading"
          v-html="singleTestimonial?.testimonial?.content"
          class="text-lg text-gray-800 main-content mb-14"
        ></div>
        <MoleculesSocialShareLinks :molecule="socialShare" />
      </main>
    </div>
    <OrganismsFlexibleContentColumns
      id="three-column-contact"
      :organism="threeColumnContact"
    />
    <MoleculesLoadingOverlay v-if="loading" />
  </div>
</template>

<style>
.single-testimonial {
  .video-wrapper {
    iframe {
      @apply w-full aspect-video;
    }
  }

  .main-content {
    h1,
    h2,
    h3,
    h4,
    h5,
    h6 {
      @apply font-archer text-2xl font-bold mb-8;
    }

    h5,
    h6 {
      @apply text-xl;
    }

    p {
      @apply mb-8;
    }

    a {
      @apply text-bc-dark-blue underline;
    }

    img {
      &.alignleft {
        @apply float-left mr-8 mb-8;
      }
      &.aligncenter {
        @apply mx-auto block mb-8;
      }
      &.alignright {
        @apply float-right ml-8 mb-8;
      }
    }

    hr {
      @apply w-1/2 mb-8 bg-gray-500 mx-auto;
    }
  }

  #three-column-contact.flexible-content-columns {
    @apply mb-20 pt-20 bg-gradient-to-b from-zinc-100 to-white;

    & > div {
      @apply border-2 border-gray-300 flex flex-col items-center justify-center p-10 gap-8 bg-white;
    }

    img {
      @apply w-20;
    }

    h3 {
      @apply font-archer font-semibold text-[28px] text-gray-700 leading-none;
    }

    .description {
      @apply text-lg text-gray-700 text-center;

      a {
        @apply underline text-bc-blue;
      }
    }
  }
}
</style>

<script setup>
const { params, path } = useRoute();
import { storeToRefs } from "pinia";
const testimonials = useTestimonials();
const { singleTestimonial, singleTestimonialLoading, singleTestimonialSlug } =
  storeToRefs(testimonials);

let loading = true;

watch(singleTestimonialLoading, (newLoading, oldLoading) => {
  loading = newLoading;
});

singleTestimonialSlug.value = params.testimonial;

const breadcrumbs = useBreadcrumbs();
const { breadcrumbsList } = storeToRefs(breadcrumbs);
breadcrumbsList.value = [
  { title: "Testimonials", link: "/lifestyle/reviews/" },
];

const threeColumnContact = {
  type: "flexible-content-columns",
  containerWidth: "full-width",
  item: [
    {
      backgroundColor: "#ffffff",
      button1: true,
      button1Color: "button-blue",
      button1Target: "_self",
      button1Text: "View All Homes",
      fieldGroupName: "item",
      link1: "/our-homes/quick-move-in-homes/",
      title1: "Explore Quick Move-In Homes",
      image: {
        altText: "",
        databaseId: 15340,
        id: "cG9zdDoxNTM0MA==",
        sourceUrl:
          "https://wp.livebouldercreek.com/wp-content/uploads/2023/04/icon-key@3x.png",
      },
    },
    {
      backgroundColor: "#ffffff",
      button1: true,
      button1Color: "button-blue",
      button1Target: "_self",
      button1Text: "View All Locations",
      description: "",
      fieldGroupName: "item",
      link1: "/contact-us/our-locations/",
      title1: "Find a Model Home Near You",
      image: {
        altText: "",
        databaseId: 15339,
        id: "cG9zdDoxNTMzOQ==",
        sourceUrl:
          "https://wp.livebouldercreek.com/wp-content/uploads/2023/04/icon-house@3x.png",
      },
    },
    {
      backgroundColor: "#ffffff",
      button1: true,
      button1Color: "button-blue",
      button1Target: "_self",
      button1Text: "View FAQ",
      description: "<p>We're here to help.</p>\n",
      fieldGroupName: "item",
      link1: "/contact-us/faq/",
      title1: "See Frequently Asked Questions",
      image: {
        altText: "question-mark icon",
        sourceUrl: "/img/question-mark.svg",
      },
    },
  ],
};

const brandWindow = computed(() => {
  const post = singleTestimonial?.value?.testimonial;
  if (!post) {
    return;
  }
  return {
    backgroundColor: "#ffffff",
    backgroundImageOpacity: 1,
    brandWindowLayout: "text-overlay-center",
    button1Color: "button-blue",
    button1Target: "_self",
    button2Color: "button-white",
    button2Target: "_self",
    button3Target: "_self",
    containerWidth: "full-width",
    contentTheme: "dark-text",
    title1: post.title,
    title2: "",
    type: "brand-window",
    backgroundImage: post.customPostDetails.image ?? {
      sourceUrl:
        "https://wp.livebouldercreek.com/wp-content/uploads/2022/08/BW_Calmante-Open-SpaceBiker_Lg-scaled.jpg",
    },
  };
});

const socialShare = computed(() => {
  const post = singleTestimonial?.value?.testimonial;

  const shareData = post
    ? {
        shareUrl: "https://livebouldercreek.com/" + path,
        title: post.title,
        imgSrc: post.customPostDetails.image?.sourceUrl,
      }
    : {
        shareUrl: "https://livebouldercreek.com/",
        title: "Visit Boulder Creek Neighorhoods",
        imgSrc:
          "https://wp.livebouldercreek.com/wp-content/uploads/2022/02/BW-Homepage-BW-slider_RF_ComingSoon_1-1024x459.jpg",
      };

  return shareData;
});
</script>
